/**
 * Live snapshots over Server-Sent Events, with a polling fallback.
 *
 * EventSource reconnects on its own, but it cannot tell us *why* it dropped.
 * If the reason was a lost session — expired, signed out elsewhere, or the
 * account suspended — it would otherwise reconnect forever against a 401, so we
 * watch for repeated failures and hand control back to the caller to re-check
 * the session.
 */

import type { Snapshot } from '@proxmon/shared';

export interface StreamHandlers {
  onSnapshot(snapshot: Snapshot): void;
  /** fired when the stream has failed repeatedly; re-check auth and restart */
  onLost(): void;
  onStateChange(connected: boolean): void;
}

const MAX_CONSECUTIVE_FAILURES = 3;

export function openSnapshotStream(handlers: StreamHandlers): () => void {
  let source: EventSource | null = null;
  let failures = 0;
  let closed = false;
  let retryTimer: number | undefined;

  const connect = (): void => {
    if (closed) return;
    source = new EventSource('/api/stream', { withCredentials: true });

    source.addEventListener('snapshot', (event) => {
      failures = 0;
      handlers.onStateChange(true);
      try {
        handlers.onSnapshot(JSON.parse((event as MessageEvent<string>).data) as Snapshot);
      } catch {
        // A truncated frame is not worth tearing the stream down for.
      }
    });

    source.addEventListener('ping', () => {
      failures = 0;
      handlers.onStateChange(true);
    });

    source.onopen = () => {
      failures = 0;
      handlers.onStateChange(true);
    };

    source.onerror = () => {
      handlers.onStateChange(false);
      source?.close();
      source = null;
      failures += 1;
      if (failures >= MAX_CONSECUTIVE_FAILURES) {
        handlers.onLost();
        return;
      }
      // Back off a little; EventSource's own retry is too eager for a server
      // that is restarting.
      retryTimer = window.setTimeout(connect, Math.min(1000 * failures, 5000));
    };
  };

  connect();

  return () => {
    closed = true;
    window.clearTimeout(retryTimer);
    source?.close();
  };
}
